<template>
  <AnalyticsCard
    title="Cancellations"
    subtitle="Who called it off, and why"
    :loading="loading"
    :empty="!loading && total === 0"
    empty-title="Nothing canceled in this period"
    empty-hint="A clean run. When cancellations do happen, this splits yours from your attendees'."
    :height="264"
  >
    <div class="p-5">
      <!-- Split first, reasons second. A high attendee cancellation rate and a
           high host one are different problems with different fixes, so the two
           are never added into a single number. -->
      <div class="flex h-2.5 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-800">
        <span v-for="part in parts" :key="part.label" class="h-full transition-[width] duration-500"
          :style="{ width: `${(part.count / total) * 100}%`, backgroundColor: part.color }"></span>
      </div>

      <div class="mt-4 grid grid-cols-3 gap-3">
        <div v-for="part in parts" :key="`${part.label}-tile`">
          <div class="flex items-center gap-1.5">
            <span class="w-2 h-2 rounded-full flex-shrink-0" :style="{ backgroundColor: part.color }"></span>
            <span class="text-[11px] text-gray-500 dark:text-gray-400 truncate">{{ part.label }}</span>
          </div>
          <p class="mt-0.5 text-lg font-bold text-gray-900 dark:text-white tabular-nums leading-none">{{ part.count }}</p>
        </div>
      </div>

      <div v-if="data.reasons?.length" class="mt-5 pt-4 border-t border-gray-100 dark:border-gray-800">
        <p class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2.5">Reasons given</p>
        <div class="space-y-2">
          <div v-for="reason in data.reasons" :key="reason.reason" class="flex items-start gap-2.5">
            <span class="text-sm text-gray-600 dark:text-gray-300 flex-1 min-w-0 line-clamp-2">{{ reason.reason }}</span>
            <span class="text-xs font-semibold text-gray-500 dark:text-gray-400 tabular-nums flex-shrink-0">{{ reason.count }}</span>
          </div>
        </div>
      </div>
      <p v-else class="mt-5 pt-4 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-400 dark:text-gray-500">
        No reasons were given. Reasons only appear when an attendee types one while canceling.
      </p>
    </div>
  </AnalyticsCard>
</template>

<script setup>
import { computed } from 'vue'
import AnalyticsCard from '../shared/AnalyticsCard.vue'
import { PALETTE } from '../shared/useAnalyticsChart.js'

const props = defineProps({
  data:    { type: Object, default: () => ({}) },
  loading: { type: Boolean, default: false },
})

const total = computed(() => props.data?.total || 0)

const parts = computed(() => [
  { label: 'By attendee',  count: props.data?.by_attendee || 0,  color: PALETTE.amber },
  { label: 'By you',       count: props.data?.by_host || 0,      color: PALETTE.red },
  // Bookings removed from a synced calendar arrive with no actor recorded.
  // Folding them into either side would invent an attribution.
  { label: 'Unattributed', count: props.data?.unattributed || 0, color: PALETTE.slate },
])
</script>
